import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  useTheme,
  FormControl,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  CircularProgress,
  Chip,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import { AssignedCampaign } from "../../types/campaign";
import { useGetInsertionOrdersForCampaignQuery } from "../../store/apis/dv360Api";
import {
  CTV_SUPPORTED_INTERNAL_PLATFORMS,
  GOOGLE_ADS_SUPPORTED_INTERNAL_PLATFORMS,
  getInternalPlatformDisplayName,
  InternalPlatform,
  isDv360InternalPlatform,
  isGoogleAdsInternalPlatform,
  resolveAssignedInternalPlatform,
} from "../../utils/internalPlatformUtils";

interface AssignedCampaignsTableProps {
  assignedCampaigns: AssignedCampaign[];
  handleRemoveCampaign: (id: string) => void;

  updateAssignedCampaign: (updatedCampaign: AssignedCampaign) => void;
  parentStartDate: string;
  parentEndDate: string;
  dv360InsertionOrderSelections?: Record<string, string[]>;
  onDv360InsertionOrderChange?: (
    campaignId: string,
    insertionOrderIds: string[]
  ) => void;
  dv360SelectionErrors?: Record<string, string | undefined>;
  enableInternalPlatformSelection?: boolean;
}

// Type assertion function to ensure we're working with the correct type
const asAssignedCampaign = (
  campaign: Partial<AssignedCampaign> & {
    id: string;
    name: string;
    platform: components["schemas"]["Platform"];
  }
): AssignedCampaign => campaign as AssignedCampaign;

// Interface for expanded state
interface ExpandedState {
  [key: string]: boolean;
}

const AssignedCampaignsTable: React.FC<AssignedCampaignsTableProps> = ({
  assignedCampaigns,
  handleRemoveCampaign,
  updateAssignedCampaign,
  parentStartDate,
  parentEndDate,
  dv360InsertionOrderSelections,
  onDv360InsertionOrderChange,
  dv360SelectionErrors,
  enableInternalPlatformSelection = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // State to track which accordions are expanded
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Effect to automatically expand newly added campaigns
  useEffect(() => {
    // Update expanded state when assignedCampaigns changes
    setExpanded((prevExpanded) => {
      const newExpandedState: ExpandedState = {};

      // Set all campaigns to be expanded by default
      assignedCampaigns.forEach((campaign) => {
        // If a campaign is not in the expanded state yet, set it to expanded (true)
        if (prevExpanded[campaign.id] === undefined) {
          newExpandedState[campaign.id] = true;
        } else {
          // Keep existing expansion state
          newExpandedState[campaign.id] = prevExpanded[campaign.id];
        }
      });

      return newExpandedState;
    });
  }, [assignedCampaigns]);

  // Toggle accordion expansion
  const toggleAccordion = (campaignId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [campaignId]: !prev[campaignId],
    }));
  };

  const handleDateChange = (
    campaignId: string,
    field: "startDate" | "endDate",
    value: string
  ) => {
    const campaign = assignedCampaigns.find((c) => c.id === campaignId);
    if (campaign) {
      updateAssignedCampaign(
        asAssignedCampaign({
          ...campaign,
          [field]: value,
        })
      );
    }
  };

  const handleMarginChange = (campaignId: string, value: string) => {
    const newValue = parseFloat(value);
    const campaign = assignedCampaigns.find((c) => c.id === campaignId);
    if (campaign) {
      updateAssignedCampaign(
        asAssignedCampaign({
          ...campaign,
          margin: isNaN(newValue) ? 0 : newValue,
        })
      );
    }
  };

  const handleInternalPlatformSelect = (
    campaign: AssignedCampaign,
    event: SelectChangeEvent<InternalPlatform>
  ) => {
    const nextPlatform = event.target.value as InternalPlatform;
    updateAssignedCampaign(
      asAssignedCampaign({
        ...campaign,
        internalPlatform: nextPlatform,
        dv360InsertionOrderIds: isDv360InternalPlatform(nextPlatform)
          ? campaign.dv360InsertionOrderIds || []
          : [],
      })
    );
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: theme.palette.grey[900],
          pb: 1,
        }}
      >
        {t("campaigns.assignedCampaigns") || "Assigned Campaigns"}
      </Typography>
      <Box
        sx={{
          width: "100%",
          padding: 1.5,
          background: theme.palette.background.paper,
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
          borderRadius: 1,
          overflow: "hidden",
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            paddingX: 1,
            paddingY: 0.5,
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              color: "black",
              fontSize: 14,

              fontWeight: 700,
            }}
          >
            {t("campaigns.name") || "Nombre de campaña"}
          </Typography>
          <Typography
            sx={{
              width: 91,
              textAlign: "center",
              color: "black",
              fontSize: 14,

              fontWeight: 700,
            }}
          >
            {t("common.actions") || "Acción"}
          </Typography>
        </Box>

        {/* Campaign List */}
        <Box
          sx={{
            height: "auto",
            maxHeight: 660,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1.5,
          }}
        >
          {assignedCampaigns.length > 0 ? (
            assignedCampaigns.map((campaign) => (
              <React.Fragment key={campaign.id}>
                {(() => {
                  const resolvedInternalPlatform =
                    resolveAssignedInternalPlatform(campaign);
                  const platformLabel = getInternalPlatformDisplayName(
                    resolvedInternalPlatform
                  );
                  const showDv360Selector =
                    isDv360InternalPlatform(resolvedInternalPlatform) &&
                    dv360InsertionOrderSelections &&
                    onDv360InsertionOrderChange;

                  const isGoogleAds = isGoogleAdsInternalPlatform(
                    resolvedInternalPlatform
                  );
                  const showPlatformSelector =
                    enableInternalPlatformSelection || isGoogleAds;
                  const platformOptions = isGoogleAds
                    ? GOOGLE_ADS_SUPPORTED_INTERNAL_PLATFORMS
                    : CTV_SUPPORTED_INTERNAL_PLATFORMS;

                  return (
                    <>
                      {/* Accordion Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          paddingX: 1,
                          background: expanded[campaign.id]
                            ? theme.palette.grey[200]
                            : "transparent",
                          borderRadius: 1,
                        }}
                      >
                        <IconButton
                          onClick={() => toggleAccordion(campaign.id)}
                          size="small"
                        >
                          {expanded[campaign.id] ? (
                            <KeyboardArrowDownIcon color="primary" />
                          ) : (
                            <KeyboardArrowRightIcon color="primary" />
                          )}
                        </IconButton>
                        <Box
                          sx={{
                            flex: 1,
                            height: 59,
                            padding: 1.5,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color: theme.palette.grey[900],
                              fontSize: 14,

                              fontWeight: 700,
                            }}
                          >
                            {campaign.name}
                          </Typography>
                          <Chip
                            label={platformLabel}
                            size="small"
                            color={
                              isDv360InternalPlatform(resolvedInternalPlatform)
                                ? "primary"
                                : "default"
                            }
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box
                          sx={{
                            paddingX: 2,
                            paddingY: 1,
                            borderRadius: 25,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
                            "&:hover": { cursor: "pointer" },
                          }}
                          onClick={() => handleRemoveCampaign(campaign.id)}
                        >
                          <CloseIcon
                            sx={{ color: theme.palette.error.main }}
                            fontSize="small"
                          />
                          <Typography
                            sx={{
                              color: theme.palette.error.main,
                              fontSize: 14,

                              fontWeight: 700,
                            }}
                          >
                            {t("common.remove") || "Quitar"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Accordion Content */}
                      {expanded[campaign.id] && (
                        <Box
                          sx={{
                            paddingX: 4,
                            paddingY: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {showPlatformSelector && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.grey[900],
                                  fontSize: 14,

                                  fontWeight: 700,
                                }}
                              >
                                {t("campaigns.internalPlatform", {
                                  defaultValue: "Internal Platform",
                                })}
                              </Typography>
                              <FormControl size="small" sx={{ maxWidth: 240 }}>
                                <Select
                                  value={
                                    resolveAssignedInternalPlatform(campaign) ||
                                    (isGoogleAds
                                      ? "GOOGLE_ADS_SEM"
                                      : "DV360_DISPLAY_BA")
                                  }
                                  onChange={(event) =>
                                    handleInternalPlatformSelect(
                                      campaign,
                                      event as SelectChangeEvent<InternalPlatform>
                                    )
                                  }
                                >
                                  {platformOptions.map((platform) => (
                                    <MenuItem key={platform} value={platform}>
                                      {getInternalPlatformDisplayName(platform)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}
                          {showDv360Selector && (
                            <Dv360InsertionOrderSelector
                              campaign={campaign}
                              selectedInsertionOrderIds={
                                dv360InsertionOrderSelections[campaign.id] || []
                              }
                              onSelectionChange={(ids) =>
                                onDv360InsertionOrderChange(campaign.id, ids)
                              }
                              errorMessage={
                                dv360SelectionErrors?.[campaign.id] || undefined
                              }
                            />
                          )}
                          {/* Row 1: Margin and Dates */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {/* Margin Field */}
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: theme.palette.grey[900],
                                    fontSize: 16,

                                    fontWeight: 700,
                                  }}
                                >
                                  {t("campaigns.margin") || "Margen (%)"}
                                </Typography>
                                <InfoOutlinedIcon
                                  sx={{
                                    color: theme.palette.grey[500],
                                    fontSize: 16,
                                  }}
                                />
                              </Box>
                              <TextField
                                fullWidth
                                placeholder="0-100"
                                type="number"
                                value={campaign.margin || 0}
                                onChange={(e) =>
                                  handleMarginChange(
                                    campaign.id,
                                    e.target.value
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                }}
                                InputProps={{
                                  sx: {
                                    borderRadius: 25,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 25,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                  },
                                }}
                              />
                            </Box>

                            {/* Start Date */}
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.grey[900],
                                  fontSize: 16,

                                  fontWeight: 700,
                                }}
                              >
                                {t("campaigns.startDate") || "Fecha de incio"}
                              </Typography>
                              <TextField
                                fullWidth
                                type="date"
                                value={campaign.startDate}
                                onChange={(e) =>
                                  handleDateChange(
                                    campaign.id,
                                    "startDate",
                                    e.target.value
                                  )
                                }
                                inputProps={{
                                  min: parentStartDate,
                                  max: campaign.endDate || parentEndDate,
                                }}
                                InputProps={{
                                  sx: {
                                    borderRadius: 25,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 25,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                  },
                                }}
                              />
                            </Box>

                            {/* End Date */}
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.grey[900],
                                  fontSize: 16,

                                  fontWeight: 700,
                                }}
                              >
                                {t("campaigns.endDate") ||
                                  "Fecha de finalización"}
                              </Typography>
                              <TextField
                                fullWidth
                                type="date"
                                value={campaign.endDate}
                                onChange={(e) =>
                                  handleDateChange(
                                    campaign.id,
                                    "endDate",
                                    e.target.value
                                  )
                                }
                                inputProps={{
                                  min: campaign.startDate || parentStartDate,
                                  max: parentEndDate,
                                }}
                                InputProps={{
                                  sx: {
                                    borderRadius: 25,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 25,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </React.Fragment>
            ))
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.grey[800],
                }}
              >
                {t("campaigns.noAssignedCampaigns") ||
                  "No campaigns have been assigned yet. Select campaigns from the list above."}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AssignedCampaignsTable;

interface Dv360InsertionOrderSelectorProps {
  campaign: AssignedCampaign;
  selectedInsertionOrderIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  errorMessage?: string;
}

const extractDv360Identifiers = (
  campaign: AssignedCampaign
): { advertiserId?: string; campaignId?: string } => {
  const parseOriginalData = (
    originalData: unknown
  ): Record<string, unknown> | undefined => {
    if (!originalData) {
      return undefined;
    }
    if (typeof originalData === "string") {
      try {
        return JSON.parse(originalData) as Record<string, unknown>;
      } catch (error) {
        console.error("Failed to parse DV360 originalData", error);
        return undefined;
      }
    }
    if (typeof originalData === "object") {
      return originalData as Record<string, unknown>;
    }
    return undefined;
  };

  const original = parseOriginalData(
    (campaign as { originalData?: unknown }).originalData
  );

  if (!original) {
    return { campaignId: campaign.id };
  }

  const advertiserIdCandidates: (string | undefined)[] = [
    typeof original.advertiserId === "string"
      ? (original.advertiserId as string)
      : undefined,
    typeof original.advertiser_id === "string"
      ? (original.advertiser_id as string)
      : undefined,
  ];

  const advertiserObject = original.advertiser as
    | Record<string, unknown>
    | undefined;
  if (advertiserObject) {
    advertiserIdCandidates.push(
      typeof advertiserObject.advertiserId === "string"
        ? (advertiserObject.advertiserId as string)
        : undefined
    );
    advertiserIdCandidates.push(
      typeof advertiserObject.id === "string"
        ? (advertiserObject.id as string)
        : undefined
    );
  }

  const advertiserId = advertiserIdCandidates.find((value): value is string =>
    Boolean(value && value.trim())
  );

  const campaignIdCandidates: (string | undefined)[] = [
    typeof original.campaignId === "string"
      ? (original.campaignId as string)
      : undefined,
    typeof original.campaign_id === "string"
      ? (original.campaign_id as string)
      : undefined,
  ];

  const originalCampaignObject = original.campaign as
    | Record<string, unknown>
    | undefined;
  if (originalCampaignObject) {
    campaignIdCandidates.push(
      typeof originalCampaignObject.campaignId === "string"
        ? (originalCampaignObject.campaignId as string)
        : undefined
    );
    campaignIdCandidates.push(
      typeof originalCampaignObject.id === "string"
        ? (originalCampaignObject.id as string)
        : undefined
    );
  }

  const campaignId =
    campaignIdCandidates.find((value): value is string =>
      Boolean(value && value.trim())
    ) || campaign.id;

  return { advertiserId, campaignId };
};

const Dv360InsertionOrderSelector: React.FC<
  Dv360InsertionOrderSelectorProps
> = ({
  campaign,
  selectedInsertionOrderIds,
  onSelectionChange,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { advertiserId, campaignId } = useMemo(
    () => extractDv360Identifiers(campaign),
    [campaign]
  );

  const {
    data: insertionOrdersResponse,
    isLoading,
    isError,
  } = useGetInsertionOrdersForCampaignQuery(
    { advertiserId: advertiserId ?? "", campaignId: campaignId ?? "" },
    {
      skip: !advertiserId || !campaignId,
    }
  );

  const insertionOrders = insertionOrdersResponse?.data ?? [];

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    if (Array.isArray(value)) {
      onSelectionChange(value as string[]);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          color: theme.palette.grey[900],
        }}
      >
        {t("campaigns.dv360InsertionOrders", {
          defaultValue: "DV360 Insertion Orders",
        })}
      </Typography>
      <FormControl
        fullWidth
        error={Boolean(errorMessage)}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,

            fontSize: 16,
            "& fieldset": {
              borderColor: theme.palette.grey[300],
            },
          },
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <FormHelperText>
            {t("campaigns.errors.failedToLoadInsertionOrders", {
              defaultValue: "Failed to load insertion orders",
            })}
          </FormHelperText>
        ) : (
          <Select
            multiple
            value={selectedInsertionOrderIds}
            onChange={handleSelectChange}
            displayEmpty
            renderValue={(selected) => {
              const selectedIds = selected as string[];
              if (selectedIds.length === 0) {
                return (
                  <Typography
                    sx={{
                      color: theme.palette.grey[500],
                    }}
                  >
                    {t("campaigns.selectInsertionOrders", {
                      defaultValue: "Select insertion orders",
                    })}
                  </Typography>
                );
              }
              const selectedNames = insertionOrders
                .filter((order) =>
                  selectedIds.includes(order?.insertionOrderId ?? "")
                )
                .map((order) => order?.displayName || order?.insertionOrderId)
                .filter((name): name is string => Boolean(name));
              return selectedNames.join(", ");
            }}
          >
            {insertionOrders.map((order) => {
              const value = order?.insertionOrderId ?? "";
              const isSelected = selectedInsertionOrderIds.includes(value);
              return (
                <MenuItem key={value} value={value} dense>
                  <Checkbox checked={isSelected} size="small" />
                  <ListItemText
                    primary={order?.displayName || value}
                    secondary={value}
                  />
                </MenuItem>
              );
            })}
          </Select>
        )}
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        {!advertiserId && (
          <FormHelperText>
            {t("campaigns.missingAdvertiserId", {
              defaultValue:
                "Advertiser information is missing for this campaign",
            })}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
